import { cn } from '../lib/cn';
import { useT } from '../i18n/provider';

/**
 * `13 §6`: esqueletos en la primera carga, nunca un spinner a pantalla
 * completa. Un spinner le dice al usuario que espere; un esqueleto le dice qué
 * está a punto de aparecer.
 *
 * El brillo que lo recorre es lo que separa un esqueleto de un rectángulo gris
 * roto: va con `transform` sobre un elemento con `overflow-hidden`, así que
 * corre en el compositor y no cuesta repintados.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded-md bg-muted', className)} aria-hidden="true">
      <span className="animate-shimmer absolute inset-0 -translate-x-full bg-linear-90 from-transparent via-ink/[0.06] to-transparent" />
    </div>
  );
}

export function SkeletonRows({ rows = 3 }: { rows?: number }) {
  const t = useT();

  return (
    <div className="space-y-3" role="status" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

/** El esqueleto de una tabla, que no tiene la misma silueta que una lista. */
export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  const t = useT();

  return (
    <div className="space-y-2 rounded-lg border border-line bg-raised p-4" role="status" aria-label={t('common.loading')}>
      <div className="flex gap-3 border-b border-line pb-3">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex gap-3 py-2">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
